import {
  StudioProject,
  ProjectPhase,
  PhaseStatus,
  AgentConfig,
  WorkflowState,
  WorkflowError,
  AgentOrchestrationConfig,
  ProjectContext,
  PhaseResults,
  ProjectArtifact,
  AgentExecutionPlan,
} from '../models/genericStudio';
import { CreativeStudioAgentService } from './creativeStudioAgentService';

const WORKFLOW_STORAGE_KEY = 'workflowStates';
const PROJECT_STORAGE_KEY = 'studioProjects';

export class WorkflowEngine {
  private static orchestrationConfig: AgentOrchestrationConfig = {
    maxParallel: 3,
    retryAttempts: 2,
    timeoutMs: 300000, // 5 minutes
    errorHandling: 'continue',
  };

  // Project management
  static saveProject(project: StudioProject): void {
    const projects = this.getAllProjects();
    const index = projects.findIndex(p => p.id === project.id);
    
    if (index >= 0) {
      projects[index] = project;
    } else {
      projects.push(project);
    }
    
    project.updatedAt = new Date().toISOString();
    localStorage.setItem(PROJECT_STORAGE_KEY, JSON.stringify(projects));
  }

  static getProject(projectId: string): StudioProject | null {
    const projects = this.getAllProjects();
    return projects.find(p => p.id === projectId) || null;
  }

  static getAllProjects(): StudioProject[] {
    const stored = localStorage.getItem(PROJECT_STORAGE_KEY);
    if (!stored) return [];
    
    // Convert Maps back from JSON
    const projects = JSON.parse(stored);
    return projects.map((p: any) => ({
      ...p,
      agentSessions: new Map(Object.entries(p.agentSessions || {})),
      results: {
        ...p.results,
        artifacts: p.results?.artifacts || [],
      },
    }));
  }

  static deleteProject(projectId: string): void {
    const projects = this.getAllProjects();
    const filtered = projects.filter(p => p.id !== projectId);
    localStorage.setItem(PROJECT_STORAGE_KEY, JSON.stringify(filtered));
  }

  // Workflow state management
  static getWorkflowState(projectId: string): WorkflowState | null {
    const states = this.getAllWorkflowStates();
    return states[projectId] || null;
  }

  static saveWorkflowState(state: WorkflowState): void {
    const states = this.getAllWorkflowStates();
    states[state.projectId] = state;
    localStorage.setItem(WORKFLOW_STORAGE_KEY, JSON.stringify(states));
  }

  static getAllWorkflowStates(): Record<string, WorkflowState> {
    const stored = localStorage.getItem(WORKFLOW_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  }

  // Phase execution
  static async executePhase(
    project: StudioProject,
    phaseIndex: number,
    onProgress?: (progress: number) => void
  ): Promise<PhaseResults> {
    const phase = project.phases[phaseIndex];
    if (!phase) throw new Error('Invalid phase index');

    // Update phase status
    phase.status = 'in-progress';
    phase.startedAt = new Date().toISOString();
    this.saveProject(project);

    // Create workflow state
    const workflowState: WorkflowState = {
      projectId: project.id,
      currentPhase: phaseIndex,
      progress: 0,
      startTime: new Date().toISOString(),
      elapsedTime: 0,
      errors: [],
      paused: false,
    };
    this.saveWorkflowState(workflowState);

    try {
      // Build execution plan
      const executionPlan = this.buildExecutionPlan(phase.agents);
      
      // Execute agents
      const agentResults = new Map<string, any>();
      const artifacts: ProjectArtifact[] = [];
      
      // Execute sequential groups
      for (const group of executionPlan.sequential) {
        if (workflowState.paused) break;
        
        const groupResults = await this.executeAgentGroup(
          group,
          project,
          phase,
          agentResults,
          false,
          (p) => {
            workflowState.progress = p;
            this.saveWorkflowState(workflowState);
            onProgress?.(p);
          }
        );
        
        // Merge results
        groupResults.forEach((value, key) => agentResults.set(key, value));
      }
      
      // Execute parallel groups
      for (const group of executionPlan.parallel) {
        if (workflowState.paused) break;
        
        const groupResults = await this.executeAgentGroup(
          group,
          project,
          phase,
          agentResults,
          true,
          (p) => {
            workflowState.progress = p;
            this.saveWorkflowState(workflowState);
            onProgress?.(p);
          }
        );
        
        // Merge results
        groupResults.forEach((value, key) => agentResults.set(key, value));
      }

      // Update phase status
      phase.status = 'completed';
      phase.completedAt = new Date().toISOString();
      
      const phaseResults: PhaseResults = {
        agentResults,
        summary: this.generatePhaseSummary(phase, agentResults),
        artifacts,
      };
      
      phase.results = phaseResults;
      this.saveProject(project);
      
      return phaseResults;
      
    } catch (error: any) {
      // Handle error
      phase.status = 'failed';
      phase.error = error.message;
      this.saveProject(project);
      
      workflowState.errors.push({
        phaseId: phase.id,
        agentId: workflowState.currentAgent || 'unknown',
        error: error.message,
        timestamp: new Date().toISOString(),
        retryCount: 0,
        resolved: false,
      });
      this.saveWorkflowState(workflowState);
      
      throw error;
    }
  }

  // Agent execution
  private static async executeAgentGroup(
    agents: AgentConfig[],
    project: StudioProject,
    phase: ProjectPhase,
    previousResults: Map<string, any>,
    parallel: boolean,
    onProgress: (progress: number) => void
  ): Promise<Map<string, any>> {
    const results = new Map<string, any>();
    
    if (parallel) {
      // Execute in parallel with max concurrency
      const chunks = this.chunkArray(agents, this.orchestrationConfig.maxParallel);
      
      for (const chunk of chunks) {
        const promises = chunk.map(agent => 
          this.executeAgent(agent, project, phase, previousResults)
        );
        
        const chunkResults = await Promise.allSettled(promises);
        
        chunkResults.forEach((result, index) => {
          const agent = chunk[index];
          if (result.status === 'fulfilled') {
            results.set(agent.agentType, result.value);
          } else if (agent.required) {
            throw new Error(`Required agent ${agent.agentName} failed: ${result.reason}`);
          }
        });
      }
    } else {
      // Execute sequentially
      for (const agent of agents) {
        try {
          const result = await this.executeAgent(agent, project, phase, previousResults);
          results.set(agent.agentType, result);
          previousResults.set(agent.agentType, result);
        } catch (error: any) {
          if (agent.required) {
            throw error;
          }
          console.error(`Optional agent ${agent.agentName} failed:`, error);
        }
      }
    }
    
    return results;
  }

  private static async executeAgent(
    agent: AgentConfig,
    project: StudioProject,
    phase: ProjectPhase,
    previousResults: Map<string, any>
  ): Promise<any> {
    // Create context
    const context: ProjectContext = {
      project,
      currentPhase: phase,
      previousResults,
      sharedData: {},
      userInputs: project.metadata.userInputs || {},
    };
    
    // Check dependencies
    if (agent.dependencies) {
      for (const dep of agent.dependencies) {
        if (!previousResults.has(dep)) {
          throw new Error(`Dependency ${dep} not satisfied for agent ${agent.agentName}`);
        }
      }
    }
    
    // Execute agent using the service
    console.log(`Executing agent: ${agent.agentName}`);
    
    try {
      const result = await CreativeStudioAgentService.executeAgent(agent, context);
      
      // Extract and store artifacts
      const artifacts = CreativeStudioAgentService.extractArtifacts(result);
      if (artifacts.length > 0) {
        project.results.artifacts.push(...artifacts);
        this.saveProject(project);
      }
      
      return result;
    } catch (error: any) {
      console.error(`Agent ${agent.agentName} failed:`, error);
      throw new Error(`Agent ${agent.agentName} failed: ${error.message}`);
    }
  }

  // Helper methods
  private static buildExecutionPlan(agents: AgentConfig[]): AgentExecutionPlan {
    const sequential: AgentConfig[][] = [];
    const parallel: AgentConfig[][] = [];
    
    // Group agents by dependencies
    const noDeps = agents.filter(a => !a.dependencies || a.dependencies.length === 0);
    const withDeps = agents.filter(a => a.dependencies && a.dependencies.length > 0);
    
    // Parallel agents without dependencies
    const parallelNoDeps = noDeps.filter(a => a.parallel);
    if (parallelNoDeps.length > 0) {
      parallel.push(parallelNoDeps);
    }
    
    // Sequential agents without dependencies
    const sequentialNoDeps = noDeps.filter(a => !a.parallel);
    sequentialNoDeps.forEach(a => sequential.push([a]));
    
    // TODO: Topological sort for agents with dependencies
    withDeps.forEach(a => sequential.push([a]));
    
    return {
      sequential,
      parallel,
      conditional: [],
    };
  }

  private static chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  private static generatePhaseSummary(phase: ProjectPhase, results: Map<string, any>): string {
    const successCount = Array.from(results.values()).filter(r => r.success).length;
    return `Phase "${phase.name}" completed with ${successCount}/${phase.agents.length} agents successfully executed.`;
  }

  // Project context helpers
  static createProjectContext(project: StudioProject): ProjectContext {
    return {
      project,
      currentPhase: project.phases[project.currentPhase],
      previousResults: new Map(),
      sharedData: {},
      userInputs: {},
    };
  }

  static updateProjectPhase(projectId: string, phaseIndex: number, update: Partial<ProjectPhase>): void {
    const project = this.getProject(projectId);
    if (!project) return;
    
    Object.assign(project.phases[phaseIndex], update);
    this.saveProject(project);
  }

  // Workflow control
  static pauseWorkflow(projectId: string): void {
    const state = this.getWorkflowState(projectId);
    if (state) {
      state.paused = true;
      this.saveWorkflowState(state);
    }
  }

  static resumeWorkflow(projectId: string): void {
    const state = this.getWorkflowState(projectId);
    if (state) {
      state.paused = false;
      this.saveWorkflowState(state);
    }
  }

  static resetWorkflow(projectId: string): void {
    const states = this.getAllWorkflowStates();
    delete states[projectId];
    localStorage.setItem(WORKFLOW_STORAGE_KEY, JSON.stringify(states));
  }
}